import React, { useEffect, useState } from "react";
import {
    Box,
    Container,
    Heading,
    Text,
    VStack,
    HStack,
    SimpleGrid,
} from "@chakra-ui/react";
import { Link as RouterLink } from "react-router-dom";
import PublicNavbar from "../Landing/PublicNavbar";
import PublicFooter from "../Landing/PublicFooter";
import { fetchBlogPosts } from "../../../services/strapi/queries";
import { urlForImage } from "../../../services/strapi/client";
import type { BlogPostListItem } from "../../../services/strapi/types";
import {
    DoodleUnderline,
    DoodleSparkle,
    DoodlePaperclip,
} from "../../CustomUi/DoodleIcons";

function formatDate(iso: string): string {
    return new Date(iso).toLocaleDateString("en-NG", {
        day: "numeric",
        month: "long",
        year: "numeric",
    });
}

import { usePageTitle } from "../../../hooks/usePageTitle";

const BlogPage: React.FC = () => {
    usePageTitle(
        "Notebook & Journal",
        "Read product announcements, educator guides, school operational playbooks, and tips from the Result team."
    );
    const [posts, setPosts] = useState<BlogPostListItem[] | null>(null);

    useEffect(() => {
        fetchBlogPosts().then(setPosts);
    }, []);

    return (
        <Box minH="100vh" bg="#FAF8F2">
            <PublicNavbar />

            {/* Hero */}
            <Box py={[16, 24]} bg="brand.900" color="white" position="relative" overflow="hidden">
                <Container maxW="4xl">
                    <VStack gap={5} textAlign="center">
                        <HStack gap={2} justify="center">
                            <DoodleSparkle size={20} color="#FBBF24" />
                            <Box
                                px={3.5}
                                py={1}
                                bg="whiteAlpha.150"
                                borderRadius="full"
                                border="1px solid"
                                borderColor="whiteAlpha.200"
                            >
                                <Text fontSize="xs" fontWeight="bold" letterSpacing="widest" textTransform="uppercase" color="gold.300">
                                    Result Notebook &amp; Journal
                                </Text>
                            </Box>
                            <DoodleSparkle size={20} color="#FBBF24" />
                        </HStack>

                        <Heading as="h1" fontSize={["3xl", "4xl", "5xl"]} fontWeight="extrabold">
                            Notes from the{" "}
                            <Box as="span" position="relative" display="inline-block" color="gold.400">
                                Result team
                                <DoodleUnderline position="absolute" bottom="-8px" left="0" color="#F59E0B" height="14px" />
                            </Box>
                        </Heading>
                        <Text fontSize={["md", "lg"]} opacity={0.8} maxW="2xl" lineHeight="1.7">
                            Product updates, school operational playbooks, teacher tips, and lessons learned from the field.
                        </Text>
                    </VStack>
                </Container>
            </Box>

            {/* Blog Post Grid */}
            <Box py={[16, 24]} className="notebook-dots">
                <Container maxW="6xl">
                    {posts === null ? (
                        <Text textAlign="center" color="gray.500" className="font-handwriting" fontSize="xl">
                            Loading notes from our notebook… ✏️
                        </Text>
                    ) : posts.length === 0 ? (
                        <VStack
                            gap={4}
                            py={14}
                            px={8}
                            textAlign="center"
                            bg="white"
                            borderRadius="2xl"
                            border="2.5px solid #0A3D2E"
                            boxShadow="5px 6px 0px #0A3D2E"
                            maxW="lg"
                            mx="auto"
                            position="relative"
                        >
                            <DoodlePaperclip color="#10B981" size={36} position="absolute" top="-16px" right="24px" />
                            <Heading as="h2" fontSize="2xl" fontWeight="extrabold" color="gray.900">
                                Fresh stories brewing! ☕️
                            </Heading>
                            <Text color="gray.600" fontSize="sm" lineHeight="1.8">
                                We&apos;re putting the finishing touches on our upcoming school management playbooks. Check back very soon or reach out if there&apos;s a topic you want us to unpack!
                            </Text>
                        </VStack>
                    ) : (
                        <SimpleGrid columns={[1, 2, 3]} gap={8}>
                            {posts.map((post, i) => {
                                const cover = post.coverImage
                                    ? urlForImage(post.coverImage).width(800).height(450).url()
                                    : null;
                                return (
                                    <RouterLink key={post._id} to={`/blog/${post.slug.current}`} style={{ textDecoration: "none" }}>
                                        <Box
                                            p={5}
                                            bg="white"
                                            borderRadius="2xl"
                                            border="2px solid #0A3D2E"
                                            boxShadow="4px 4px 0px #0A3D2E"
                                            style={{ transform: i % 2 === 0 ? "rotate(-0.5deg)" : "rotate(0.5deg)" }}
                                            transition="all 0.2s cubic-bezier(0.34, 1.56, 0.64, 1)"
                                            _hover={{
                                                transform: "translate(-3px, -3px) rotate(0deg) scale(1.02)",
                                                boxShadow: "6px 7px 0px #0A3D2E",
                                            }}
                                            display="block"
                                        >
                                            <VStack align="start" gap={3}>
                                            <Box
                                                w="full"
                                                aspectRatio={16 / 9}
                                                bg="#FAF8F2"
                                                borderRadius="xl"
                                                overflow="hidden"
                                                border="1.5px solid #0A3D2E"
                                                backgroundImage={cover ? `url(${cover})` : undefined}
                                                backgroundSize="cover"
                                                backgroundPosition="center"
                                            />
                                            <HStack gap={2} fontSize="xs" color="gray.500" fontWeight="bold">
                                                <Text>{formatDate(post.publishedAt)}</Text>
                                                {post.author?.name && (
                                                    <>
                                                        <Text>·</Text>
                                                        <Text color="brand.800">{post.author.name}</Text>
                                                    </>
                                                )}
                                            </HStack>
                                            <Heading as="h3" fontSize="md" fontWeight="extrabold" color="gray.900" lineHeight="1.3">
                                                {post.title}
                                            </Heading>
                                            {post.excerpt && (
                                                <Text fontSize="xs" color="gray.600" lineHeight="1.6">
                                                    {post.excerpt}
                                                </Text>
                                            )}
                                        </VStack>
                                    </Box>
                                </RouterLink>
                            );
                        })}
                        </SimpleGrid>
                    )}
                </Container>
            </Box>

            <PublicFooter />
        </Box>
    );
};

export default BlogPage;
